/**
 * Which fields are still following their source, for the form currently open.
 *
 * This has to be shared state rather than a hook: the engine that writes the
 * derived values lives on the title field, and the fields that have to say
 * "Auto" or "Yours" are rendered in unrelated parts of the form. React has no
 * way for one field to provide context to its siblings, so the answer lives
 * beside the tree and is subscribed to.
 *
 * It is deliberately not persisted. Whether a value was automatic is not a fact
 * about the document, and storing it would mean inventing a field the content
 * model does not have and that anything writing Firestore directly would leave
 * stale. When a form is opened, the values themselves are the evidence: a
 * stored value that already equals what would be derived is treated as
 * automatic, and one that disagrees is an override.
 */

import { DerivableKey } from "../derive/autofill";

type Listener = () => void;

const EMPTY: ReadonlySet<DerivableKey> = new Set();

const tracking = new Map<string, ReadonlySet<DerivableKey>>();
const listeners = new Map<string, Set<Listener>>();

const notify = (formKey: string) => {
    for (const listener of listeners.get(formKey) ?? []) listener();
};

/** Replaces the set wholesale; `useSyncExternalStore` compares by identity. */
function set(formKey: string, next: ReadonlySet<DerivableKey>): void {
    tracking.set(formKey, next);
    notify(formKey);
}

/** Called when a form is opened, with what the stored values imply. */
export function resetTracking(formKey: string, initial: Set<DerivableKey>): void {
    const current = tracking.get(formKey);
    if (current && current.size === initial.size && [...initial].every((key) => current.has(key))) {
        return;
    }
    set(formKey, initial);
}

export function stopTracking(formKey: string, field: DerivableKey): void {
    const current = tracking.get(formKey) ?? EMPTY;
    if (!current.has(field)) return;
    const next = new Set(current);
    next.delete(field);
    set(formKey, next);
}

/** Used by "use the derived value" and by clearing a field back to empty. */
export function resumeTracking(formKey: string, field: DerivableKey): void {
    const current = tracking.get(formKey) ?? EMPTY;
    if (current.has(field)) return;
    set(formKey, new Set(current).add(field));
}

export const getTracking = (formKey: string): ReadonlySet<DerivableKey> =>
    tracking.get(formKey) ?? EMPTY;

export function subscribeTracking(formKey: string, listener: Listener): () => void {
    const forKey = listeners.get(formKey) ?? new Set<Listener>();
    forKey.add(listener);
    listeners.set(formKey, forKey);
    return () => {
        forKey.delete(listener);
        if (forKey.size === 0) listeners.delete(formKey);
    };
}
